import React, { useState, useRef, useEffect } from 'react';
import api from '../utils/api';
import { Smartphone } from '@mui/icons-material';
import { TextField, InputAdornment, Box, Alert } from '@mui/material';

const textFieldStyle = {
    mb: 2.5,
    '& .MuiOutlinedInput-root': {
        borderRadius: '15px',
        backgroundColor: '#fff',
        '& fieldset': {
            borderColor: 'rgba(0, 0, 0, 0.1)',
        },
        '&:hover fieldset': {
            borderColor: 'rgba(0,0,0,0.2)',
        },
        '&.Mui-focused fieldset': {
            borderColor: '#007bff',
        },
    }
};

const Login = () => {
    const otpLength = 5;
    const [step, setStep] = useState(1); // 1: Mobile, 2: OTP
    const [phone, setPhone] = useState('');
    const [otpDigits, setOtpDigits] = useState(() => Array(otpLength).fill(''));
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const otpInputsRef = useRef([]);

    const clearOtpDigits = () => {
        setOtpDigits(Array(otpLength).fill(''));
    };

    useEffect(() => {
        if (step === 2) {
            setTimeout(() => {
                otpInputsRef.current[0]?.focus();
            }, 10);
        }
    }, [step]);

    const isPhoneValid = (value) => {
        const digits = value.replace(/\D/g, '');
        return digits.length === 11 && digits.startsWith('0');
    };

    const handleRequestOtp = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        if (!isPhoneValid(phone)) {
            setLoading(false);
            setError('شماره موبایل باید 11 رقم باشد و با 0 شروع شود');
            return;
        }
        try {
            await api.post('/auth/request-otp', { phone });
            clearOtpDigits();
            setStep(2);
        } catch (err) {
            setError(err.response?.data?.message || 'خطا در ارسال کد تایید');
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyOtp = async (e) => {
        e.preventDefault();
        const otpCode = otpDigits.join('');
        if (otpCode.length !== otpLength) {
            return;
        }
        setLoading(true);
        setError('');
        try {
            const response = await api.post('/auth/verify-otp', { phone, code: otpCode });
            const { accessToken, refreshToken, user } = response.data;

            localStorage.setItem('token', accessToken);
            localStorage.setItem('refreshToken', refreshToken);
            localStorage.setItem('user', JSON.stringify(user));

            if (!user.firstName || !user.lastName || !user.nationalityCode) {
                window.location.replace('/profile');
            } else {
                window.location.replace('/');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'کد تایید اشتباه است');
        } finally {
            setLoading(false);
        }
    };

    const handleOtpChange = (index, value) => {
        const sanitized = value.replace(/\D/g, '');
        setOtpDigits((prev) => {
            const next = [...prev];
            next[index] = sanitized ? sanitized.slice(-1) : '';
            return next;
        });
        if (sanitized && otpInputsRef.current[index + 1]) {
            otpInputsRef.current[index + 1].focus();
        }
    };

    const handleOtpKeyDown = (index, e) => {
        if (e.key === 'Backspace') {
            if (otpDigits[index]) {
                setOtpDigits((prev) => {
                    const next = [...prev];
                    next[index] = '';
                    return next;
                });
                return;
            }
            if (index > 0) {
                const previous = otpInputsRef.current[index - 1];
                if (previous) {
                    previous.focus();
                    setOtpDigits((prev) => {
                        const next = [...prev];
                        next[index - 1] = '';
                        return next;
                    });
                }
            }
        }
    };

    const handleOtpPaste = (event) => {
        event.preventDefault();
        const pasted = event.clipboardData
            .getData('text')
            .replace(/\D/g, '')
            .slice(0, otpLength);
        if (!pasted) {
            return;
        }
        setOtpDigits((prev) => {
            const next = [...prev];
            for (let i = 0; i < pasted.length; i += 1) {
                next[i] = pasted[i];
            }
            return next;
        });
        const focusIndex = Math.min(pasted.length, otpLength - 1);
        otpInputsRef.current[focusIndex]?.focus();
    };

    const isOtpComplete = otpDigits.every((digit) => digit !== '');

    return (
        <div className="main-container container" style={{ direction: "rtl" }}>
            <div className="row h-100 mb-4">
                <div className="col-12 col-md-8 mx-auto align-self-center text-center">
                    <img
                        src="/assets/img/logo.png"
                        alt=""
                        style={{
                            maxWidth: 280,
                        }}
                        className="mb-4"
                    />
                    <h1 className="mb-4">ورود / ثبت نام</h1>
                    <p className="text-secondary mb-4">لطفا شماره همراه خود را وارد کنید</p>

                    {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

                    {step === 1 ? (
                        <form onSubmit={handleRequestOtp} style={{
                            direction: "ltr"
                        }}>
                            <TextField
                                fullWidth
                                type="tel"
                                label="شماره همراه"
                                inputMode="tel"
                                value={phone}
                                onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))}
                                placeholder="0914 *** ** **"
                                required
                                sx={textFieldStyle}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Smartphone color="action" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <button
                                type="submit"
                                className="btn btn-lg btn-default w-100 shadow"
                                disabled={loading}
                            >
                                {loading ? 'در حال ارسال...' : 'ارسال کد تایید'}
                            </button>
                        </form>
                    ) : (
                        <form onSubmit={handleVerifyOtp} style={{
                            direction: "ltr"
                        }}>
                            <p className="text-secondary fw-bold mb-2">کد تایید 5 رقمی</p>
                            <Box display="flex" justifyContent="center" gap={1.25} sx={{ mb: 2 }}>
                                {otpDigits.map((digit, index) => (
                                    <input
                                        key={index}
                                        ref={(el) => (otpInputsRef.current[index] = el)}
                                        type="text"
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        maxLength={1}
                                        className="form-control text-center"
                                        value={digit}
                                        onChange={(e) => handleOtpChange(index, e.target.value)}
                                        onKeyDown={(e) => handleOtpKeyDown(index, e)}
                                        onPaste={handleOtpPaste}
                                        autoComplete={index === 0 ? 'one-time-code' : undefined}
                                        name={index === 0 ? 'one-time-code' : undefined}
                                    />
                                ))}
                            </Box>
                            <p className="text-secondary mb-3">کد ارسال شده به {phone} را وارد کنید</p>
                            <button
                                type="submit"
                                className="btn btn-lg btn-default w-100 shadow"
                                disabled={loading || !isOtpComplete}
                            >
                                {loading ? 'در حال بررسی...' : 'تایید و ورود'}
                            </button>
                            <button
                                type="button"
                                className="btn btn-link mt-3"
                                onClick={() => {
                                    setStep(1);
                                    clearOtpDigits();
                                }}
                            >
                                تغییر شماره همراه
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Login;

